import { registerLocaleData } from "@angular/common";
import { inject, Injectable, LOCALE_ID, provideAppInitializer } from "@angular/core";
import { loadTranslations } from "@angular/localize";
import { LanguageService } from "@app/language.service";
import { getStoredTranslations } from "@app/translation-cache";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
class I18n {
  private languageService = inject(LanguageService);

  public localeString: string = "en-US";

  private readonly localeMap = {
    de: () => import("@angular/common/locales/de"),
    en: () => import("@angular/common/locales/en"),
    fr: () => import("@angular/common/locales/fr"),
    es: () => import("@angular/common/locales/es"),
    pt: () => import("@angular/common/locales/pt"),
  };

  async setLocale(user: User): Promise<void> {
    this.localeString = user.language;
    const localeModule = await this.importLocaleModule();

    registerLocaleData(localeModule.default);

    await this.updateTranslationsInLocalStorage();

    const translations = getStoredTranslations(this.localeString) ?? {};
    loadTranslations(translations);
  }

  private async updateTranslationsInLocalStorage() {
    await firstValueFrom(this.languageService.updateTranslations()).catch((err) => console.error(err));
  }

  private async importLocaleModule() {
    const lang = this.localeString.split("-")[0]; // e.g., "de" from "de-DE"
    const importFn = this.localeMap[lang as keyof typeof this.localeMap] ?? this.localeMap["en"];
    return await importFn();
  }

  /*private mapLanguageIsoToWebpackLocaleFilename(localeString: string) {
    const defaultRegionLocaleStrings: string[] = [
      "en-US",
      "de-DE",
      "es-ES",
      "fr-FR",
      "it-IT",
      "pt-BR",
      "zh-CN",
      "ja-JP",
      "ru-RU",
      "ko-KR",
    ];
    return defaultRegionLocaleStrings.includes(localeString) ? `${localeString.split("-")[0]}.mjs` : `${localeString}.mjs`;
  }*/
}

function setLocale() {
  return provideAppInitializer(() => {
    const initializerFn = (
      (i18n: I18n, userService: UserService) => () =>
        firstValueFrom(userService.user$).then(
          (user) => i18n.setLocale(user),
          () => undefined, // return something in error case, so that app can be initialized
        )
    )(inject(I18n), inject(UserService));
    return initializerFn();
  });
}

function setLocaleId() {
  return {
    provide: LOCALE_ID,
    useFactory: (i18n: I18n) => i18n.localeString,
    deps: [I18n],
  };
}

export const I18nModule = {
  setLocale: setLocale,
  setLocaleId: setLocaleId,
};
